import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

export interface MarkerData {
  name: string;
  /** Actual measured value (0–100 normalized) */
  value: number;
  /** Minimum of reference range (normalized, 0–100) */
  min: number;
  /** Maximum of reference range (normalized, 0–100) */
  max: number;
  /** 'pass' | 'fail' | 'warning' */
  result: string;
}

export interface MarkerRadarChartProps {
  markers: MarkerData[];
  /** 'compact' renders 200x200; 'full' renders 400x400 */
  mode?: 'compact' | 'full';
  className?: string;
}

interface TooltipPayload {
  payload?: {
    name: string;
    value: number;
    min: number;
    max: number;
    result: string;
  };
}

function CustomTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayload[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  const resultColor =
    d.result === 'fail' ? '#ff6b6b' : d.result === 'warning' ? '#fbbf24' : '#00d4aa';

  return (
    <div
      style={{
        background: 'var(--color-bg-elevated, #1a1a1d)',
        border: '1px solid var(--color-border, #2a2a2e)',
        borderRadius: '8px',
        padding: '8px 12px',
        fontSize: '12px',
        fontFamily: 'var(--font-body, Inter, sans-serif)',
        color: 'var(--color-text, #e4e4e7)',
        boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
        pointerEvents: 'none',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>{d.name}</div>
      <div style={{ color: resultColor, fontWeight: 500 }}>Value: {d.value.toFixed(1)}%</div>
      <div style={{ color: 'var(--color-text-muted)', marginTop: '2px' }}>
        Ref: {d.min}–{d.max}%
      </div>
    </div>
  );
}

export function MarkerRadarChart({ markers, mode = 'full', className }: MarkerRadarChartProps) {
  const size = mode === 'compact' ? 200 : 400;
  const labelFontSize = mode === 'compact' ? 9 : 11;

  // Build chart data — reference (target) and actual polygons
  const chartData = markers.map((m) => ({
    name: m.name,
    actual: m.value,
    reference: m.max, // upper bound of reference range
    refMin: m.min,
    value: m.value,
    min: m.min,
    max: m.max,
    result: m.result,
  }));

  const tickFormatter = (name: string) => {
    if (mode === 'compact' && name.length > 5) return name.substring(0, 5);
    return name;
  };

  return (
    <div
      className={className}
      style={{ width: size, height: size, minWidth: size, minHeight: size }}
      aria-label="Marker profile radar chart"
    >
      <ResponsiveContainer width={size} height={size} minWidth={size} minHeight={size}>
        <RadarChart data={chartData} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
          <PolarGrid
            gridType="polygon"
            stroke="var(--color-border, #2a2a2e)"
            strokeWidth={1}
            strokeOpacity={0.6}
          />
          <PolarAngleAxis
            dataKey="name"
            tickFormatter={tickFormatter}
            tick={{
              fill: 'var(--color-text-muted, #a1a1aa)',
              fontSize: labelFontSize,
              fontFamily: 'var(--font-body, Inter, sans-serif)',
              fontWeight: 500,
            }}
          />

          {/* Reference range polygon (green) */}
          <Radar
            name="Reference"
            dataKey="reference"
            stroke="rgba(0,212,170,0.4)"
            fill="rgba(0,212,170,0.08)"
            strokeWidth={1.5}
            strokeDasharray="4 2"
            dot={false}
          />

          {/* Actual values polygon */}
          <Radar
            name="Actual"
            dataKey="actual"
            stroke="#00d4aa"
            fill="rgba(0,212,170,0.18)"
            strokeWidth={2}
            dot={(dotProps) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const item = (dotProps as any).payload as (typeof chartData)[0] | undefined;
              const isFailing = item?.result === 'fail';
              const isWarning = item?.result === 'warning';
              const dotColor = isFailing ? '#ff6b6b' : isWarning ? '#fbbf24' : '#00d4aa';
              const r = mode === 'compact' ? 3 : 4;
              return (
                <circle
                  key={`dot-${dotProps.index}`}
                  cx={dotProps.cx}
                  cy={dotProps.cy}
                  r={r}
                  fill={dotColor}
                  stroke={isFailing ? 'rgba(255,107,107,0.3)' : 'rgba(0,212,170,0.3)'}
                  strokeWidth={2}
                />
              );
            }}
          />

          {mode === 'full' && <Tooltip content={<CustomTooltip />} />}
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}
